import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Search, Loader2, Navigation, Send, Building2, X } from "lucide-react";
import { TYPE_BY_VALUE } from "../lib/propertyTypes";
import { formatUSPhone } from "../lib/phone";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const RADIUS_OPTIONS = [5, 10, 25, 50, 100];

// Public endpoints — no auth header attached.
const publicClient = axios.create({ baseURL: `${API_BASE}/api` });

export default function NearMeSearch({ theme = "dark" }) {
  const [address, setAddress] = useState("");
  const [radius, setRadius] = useState(10);
  const [busy, setBusy] = useState(false);
  const [locating, setLocating] = useState(false);
  const [results, setResults] = useState(null); // { items, center, radius_miles, resolved_address }
  const [activeLead, setActiveLead] = useState(null); // property object when lead form open

  const runSearch = async (params) => {
    setBusy(true);
    try {
      const { data } = await publicClient.get("/public/properties/nearby", { params });
      setResults(data);
      if (!data.items?.length) {
        toast.info("No properties found in that area — try a wider radius.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Search failed. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const searchByAddress = async (e) => {
    e?.preventDefault?.();
    if (!address.trim()) return;
    await runSearch({ address: address.trim(), radius_miles: radius });
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Your browser does not support geolocation.");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        setLocating(false);
        setAddress("");
        await runSearch({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          radius_miles: radius,
        });
      },
      (err) => {
        setLocating(false);
        toast.error(err.message || "Could not get your location.");
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 },
    );
  };

  const isDark = theme === "dark";
  const panelBg = isDark ? "bg-white/5 backdrop-blur-md border-white/10" : "bg-white border-slate-200";
  const inputCls = isDark
    ? "bg-white/10 border-white/20 text-white placeholder-white/40 focus:border-white/60"
    : "bg-white border-slate-300 text-slate-900 placeholder-slate-400";
  const labelCls = isDark ? "text-white/60" : "text-slate-500";

  return (
    <div className={`rounded-2xl border p-5 md:p-7 ${panelBg}`} data-testid="near-me-search">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className={`overline ${labelCls}`}>Discover</div>
          <h3 className={`text-2xl md:text-3xl font-bold tracking-tight ${isDark ? "text-white" : "text-slate-900"}`} style={{ fontFamily: "Outfit" }}>
            Properties near you.
          </h3>
          <p className={`text-sm mt-1 ${isDark ? "text-white/50" : "text-slate-500"}`}>
            Search by ZIP, address, or your current location. Contact owners directly.
          </p>
        </div>
        {results && (
          <button
            onClick={() => { setResults(null); setAddress(""); }}
            data-testid="near-me-clear"
            className={`text-xs font-mono uppercase tracking-widest flex items-center gap-1 ${isDark ? "text-white/60 hover:text-white" : "text-slate-500 hover:text-slate-900"}`}
          >
            <X className="w-3 h-3" /> Clear
          </button>
        )}
      </div>

      <form onSubmit={searchByAddress} className="mt-5 grid grid-cols-1 md:grid-cols-[1fr_auto_auto_auto] gap-2">
        <div className="relative">
          <MapPin className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? "text-white/40" : "text-slate-400"}`} />
          <input
            type="text"
            placeholder="ZIP or full address (e.g., 10001 or 350 5th Ave, NY)"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            data-testid="near-me-address-input"
            className={`w-full pl-9 pr-3 py-3 rounded-full border text-sm font-mono focus:outline-none focus:ring-2 focus:ring-offset-0 transition-colors ${inputCls}`}
          />
        </div>
        <select
          value={radius}
          onChange={(e) => setRadius(Number(e.target.value))}
          data-testid="near-me-radius-select"
          className={`px-4 py-3 rounded-full border text-sm font-mono focus:outline-none transition-colors ${inputCls}`}
        >
          {RADIUS_OPTIONS.map((r) => <option key={r} value={r} className="bg-white text-slate-900">{r} mi</option>)}
        </select>
        <button
          type="button"
          onClick={useMyLocation}
          disabled={locating || busy}
          data-testid="near-me-geolocate"
          title="Use my current location"
          className={`px-4 py-3 rounded-full border text-sm font-mono flex items-center justify-center gap-2 transition-colors disabled:opacity-50 ${
            isDark ? "border-white/20 text-white hover:bg-white/10" : "border-slate-300 text-slate-700 hover:bg-slate-50"
          }`}
        >
          {locating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Navigation className="w-4 h-4" />}
          <span className="hidden md:inline">Near me</span>
        </button>
        <button
          type="submit"
          disabled={busy || !address.trim()}
          data-testid="near-me-search-button"
          className={`px-5 py-3 rounded-full text-sm font-bold flex items-center justify-center gap-2 transition-colors disabled:opacity-50 ${
            isDark ? "bg-white text-slate-900 hover:bg-slate-100" : "bg-slate-900 text-white hover:bg-slate-800"
          }`}
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          Search
        </button>
      </form>

      {results && (
        <div className="mt-6" data-testid="near-me-results">
          <div className={`text-xs font-mono uppercase tracking-widest mb-3 ${labelCls}`}>
            {results.count || 0} propert{(results.count === 1) ? "y" : "ies"} within {results.radius_miles} mi
            {results.resolved_address && <> of <span className={isDark ? "text-white" : "text-slate-900"}>{results.resolved_address}</span></>}
          </div>
          {results.items?.length ? (
            <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {results.items.map((p) => (
                <ResultCard key={p.id} property={p} onInterested={() => setActiveLead(p)} isDark={isDark} />
              ))}
            </ul>
          ) : (
            <div className={`rounded-2xl border border-dashed p-10 text-center ${isDark ? "border-white/10 text-white/40" : "border-slate-200 text-slate-400"}`}>
              No properties in this radius. Try 25 mi or 50 mi.
            </div>
          )}
        </div>
      )}

      {activeLead && (
        <LeadDialog
          property={activeLead}
          onClose={() => setActiveLead(null)}
          isDark={isDark}
        />
      )}
    </div>
  );
}

function ResultCard({ property, onInterested, isDark }) {
  const typeMeta = TYPE_BY_VALUE[property.property_type];
  const cardBg = isDark ? "bg-white/5 border-white/10 hover:bg-white/10" : "bg-white border-slate-200 hover:border-slate-300";
  const locLine = [property.city, property.state].filter(Boolean).join(", ");
  return (
    <li
      className={`rounded-xl border p-4 transition-colors ${cardBg}`}
      data-testid={`near-me-card-${property.id}`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xl shrink-0 ${isDark ? "bg-white/10" : "bg-slate-100"}`}>
          {typeMeta?.emoji || "🏢"}
        </div>
        <div className="min-w-0 flex-1">
          <a
            href={`/listings/${property.id}`}
            className={`font-bold truncate hover:underline ${isDark ? "text-white" : "text-slate-900"}`}
            style={{ fontFamily: "Outfit" }}
            data-testid={`near-me-listing-link-${property.id}`}
          >
            {property.name}
          </a>
          <div className={`text-[11px] font-mono uppercase tracking-widest mt-0.5 ${isDark ? "text-white/50" : "text-slate-500"}`}>
            {typeMeta?.label || property.property_type}
            {property.units ? ` · ${property.units} unit${property.units === 1 ? "" : "s"}` : ""}
          </div>
          {locLine && (
            <div className={`text-xs mt-1 flex items-center gap-1 ${isDark ? "text-white/70" : "text-slate-600"}`}>
              <MapPin className="w-3 h-3" /> {locLine}
            </div>
          )}
          <div className="mt-3 flex items-center justify-between gap-2">
            <span className={`text-[10px] font-mono uppercase tracking-widest ${isDark ? "text-white/40" : "text-slate-400"}`}>
              {property.distance_miles != null ? `${property.distance_miles} mi away` : ""}
            </span>
            <div className="flex items-center gap-1.5">
              <a
                href={`/listings/${property.id}`}
                className={`text-[11px] font-mono uppercase tracking-widest px-2.5 py-1.5 rounded-full border transition-colors ${
                  isDark ? "border-white/20 text-white hover:bg-white/10" : "border-slate-300 text-slate-700 hover:bg-slate-50"
                }`}
                data-testid={`near-me-view-${property.id}`}
              >
                View
              </a>
              <button
                onClick={onInterested}
                data-testid={`near-me-interested-${property.id}`}
                className={`text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1 transition-colors ${
                  isDark ? "bg-white text-slate-900 hover:bg-slate-100" : "bg-slate-900 text-white hover:bg-slate-800"
                }`}
              >
                I'm interested <Send className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </li>
  );
}

function LeadDialog({ property, onClose, isDark }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await publicClient.post("/public/leads", {
        property_id: property.id,
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: form.message,
        source: "landing_near_me",
      });
      toast.success("Inquiry sent — the owner will be in touch.");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm animate-in fade-in"
      data-testid="near-me-lead-dialog"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl bg-white text-slate-900 p-6 shadow-2xl"
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="overline text-slate-500">Contact owner</div>
            <h4 className="text-xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>
              {property.name}
            </h4>
            <div className="text-xs font-mono text-slate-500 mt-0.5 flex items-center gap-1">
              <Building2 className="w-3 h-3" /> {[property.city, property.state].filter(Boolean).join(", ")}
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900 p-1" data-testid="near-me-lead-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={submit} className="mt-5 space-y-3 text-sm">
          <input
            required
            placeholder="Your name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full px-3 py-2 rounded-md border border-slate-200 focus:outline-none focus:border-slate-400"
            data-testid="near-me-lead-name"
          />
          <input
            required
            type="email"
            placeholder="Email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full px-3 py-2 rounded-md border border-slate-200 focus:outline-none focus:border-slate-400"
            data-testid="near-me-lead-email"
          />
          <input
            placeholder="(555) 123-4567 (optional)"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            value={formatUSPhone(form.phone)}
            onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "").slice(0, 11) })}
            className="w-full px-3 py-2 rounded-md border border-slate-200 focus:outline-none focus:border-slate-400"
            data-testid="near-me-lead-phone"
          />
          <textarea
            rows={3}
            placeholder="What would you like to know?"
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            className="w-full px-3 py-2 rounded-md border border-slate-200 focus:outline-none focus:border-slate-400"
            data-testid="near-me-lead-message"
          />
          <button
            type="submit"
            disabled={busy}
            data-testid="near-me-lead-submit"
            className="w-full py-2.5 rounded-md bg-slate-900 text-white font-bold hover:bg-slate-800 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Send inquiry
          </button>
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400 text-center">
            We never share your info. Reply comes from the property owner.
          </div>
        </form>
      </div>
    </div>
  );
}

// Suppress theme prop warning
NearMeSearch.defaultProps = { theme: "dark" };
